import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { toast } from "sonner";
import { Loader2, ChevronRight, Check, ExternalLink } from "lucide-react";

const apiBase = process.env.REACT_APP_BACKEND_URL || "";

const METHODOLOGIES = [
  { value: "agile", label: "Agile", desc: "Iterative sprints, continuous delivery" },
  { value: "waterfall", label: "Waterfall", desc: "Sequential phases, milestone-driven" },
  { value: "hybrid", label: "Hybrid", desc: "Best of both worlds" },
];

const ROLES = ["Admin", "Member", "Viewer"];

function capitalize(str) {
  if (!str) return "";
  return str.charAt(0).toUpperCase() + str.slice(1);
}

const OnboardingWizard = () => {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();

  const [visible, setVisible] = useState(false);
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);

  // Step 1
  const [workspaceName, setWorkspaceName] = useState("");

  // Step 2 — Connect tools
  const [toolsStatus, setToolsStatus] = useState({ hubspot: false, xero: false });
  const [toolsChecking, setToolsChecking] = useState(false);

  // Step 3
  const [projectName, setProjectName] = useState("");
  const [methodology, setMethodology] = useState("agile");
  const [createdProjectId, setCreatedProjectId] = useState(null);
  const [showPhases, setShowPhases] = useState(false);
  const [phasesText, setPhasesText] = useState("");

  // Step 4
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("member");

  // On mount: check if this user needs onboarding
  useEffect(() => {
    if (!user) return;
    if (user.onboarded === true) return;

    const emailPrefix = (user.email || "").split("@")[0] || "My";
    setWorkspaceName(capitalize(emailPrefix) + "'s Workspace");

    (async () => {
      try {
        const { data } = await api.get("/projects");
        if (!data || data.length === 0) {
          setVisible(true);
        }
      } catch {
        // If projects fetch fails, don't show wizard
      }
    })();
  }, [user]);

  const checkToolsStatus = async () => {
    setToolsChecking(true);
    try {
      const [hs, xero] = await Promise.all([
        api.get("/integrations/hubspot/status").catch(() => ({ data: { connected: false } })),
        api.get("/integrations/xero/status").catch(() => ({ data: { connected: false } })),
      ]);
      setToolsStatus({ hubspot: !!hs.data?.connected, xero: !!xero.data?.connected });
    } finally {
      setToolsChecking(false);
    }
  };

  // Re-check connection status whenever this step is active and the window
  // regains focus — connecting HubSpot/Xero opens a new tab (OAuth is a
  // full-page redirect), so the wizard needs to notice when the user comes back.
  useEffect(() => {
    if (!visible || step !== 2) return;
    checkToolsStatus();
    window.addEventListener("focus", checkToolsStatus);
    return () => window.removeEventListener("focus", checkToolsStatus);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, step]);

  if (!visible) return null;

  const handleStep1 = async () => {
    if (!workspaceName.trim()) { toast.error("Please enter a workspace name"); return; }
    setBusy(true);
    try {
      await api.patch("/workspace/name", { workspace_name: workspaceName.trim() });
      setStep(2);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update workspace name");
    } finally {
      setBusy(false);
    }
  };

  const handleStep3 = async () => {
    if (!projectName.trim()) { toast.error("Please enter a project name"); return; }
    const phases = phasesText.split("\n").map((s) => s.trim()).filter(Boolean);
    setBusy(true);
    try {
      const { data } = await api.post("/projects", {
        name: projectName.trim(),
        methodology,
        status: "planning",
        phases: phases.length ? phases : null,
      });
      setCreatedProjectId(data.project_id);
      setStep(4);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create project");
    } finally {
      setBusy(false);
    }
  };

  const finishOnboarding = async (sendInvite) => {
    setBusy(true);
    try {
      if (sendInvite && inviteEmail.trim()) {
        try {
          await api.post("/team/invitations", { email: inviteEmail.trim(), role: inviteRole });
          toast.success("Invite sent!");
        } catch (e) {
          toast.error(e.response?.data?.detail || "Failed to send invite");
        }
      }
      // Mark onboarded
      try {
        const { data: updatedUser } = await api.patch("/users/me/onboarded", {});
        if (updatedUser && setUser) setUser(updatedUser);
      } catch {
        // non-fatal
      }
      setVisible(false);
      if (createdProjectId) {
        navigate(`/workspace/${createdProjectId}`);
      } else {
        navigate("/dashboard");
      }
    } finally {
      setBusy(false);
    }
  };

  const STEPS = ["Workspace", "Connect tools", "Project", "Invite"];

  return (
    <div className="fixed inset-0 z-[9999] bg-gray-900/50 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="relative w-full max-w-[520px] bg-white border border-gray-100 rounded-2xl shadow-xl shadow-gray-900/10 overflow-hidden">

        {/* Progress header */}
        <div className="px-7 pt-6">
          <div className="flex items-center gap-2 mb-5">
            {STEPS.map((s, i) => {
              const idx = i + 1;
              const done = step > idx;
              const active = step === idx;
              return (
                <div key={s} className="flex items-center gap-2">
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold border ${
                    done
                      ? "bg-violet-600 border-violet-600 text-white"
                      : active
                      ? "bg-violet-100 border-violet-300 text-violet-600"
                      : "bg-gray-50 border-gray-200 text-gray-400"
                  }`}>
                    {done ? <Check size={14} /> : idx}
                  </div>
                  <span className={`text-xs ${active ? "text-gray-900 font-semibold" : done ? "text-violet-600" : "text-gray-400"}`}>{s}</span>
                  {i < STEPS.length - 1 && (
                    <div className={`w-6 h-px ml-1 ${done ? "bg-violet-600" : "bg-gray-200"}`} />
                  )}
                </div>
              );
            })}
          </div>
          <div className="text-[10px] text-gray-400 uppercase tracking-[0.2em] font-mono mb-1">
            Step {step} of 4
          </div>
        </div>

        <div className="px-7 pb-7">

          {/* ── Step 1: Workspace ── */}
          {step === 1 && (
            <div>
              <h1 className="text-[22px] font-bold text-gray-900 mb-1.5">Welcome to CombinedWork 👋</h1>
              <p className="text-[13px] text-gray-500 mb-7 leading-relaxed">Let's set up your workspace in 2 minutes.</p>
              <label className="block text-xs text-gray-500 mb-1.5 font-medium">Workspace name</label>
              <input
                type="text"
                value={workspaceName}
                onChange={(e) => setWorkspaceName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleStep1()}
                autoFocus
                data-testid="onboarding-workspace-name"
                className="w-full box-border py-2.5 px-3.5 rounded-lg border border-gray-200 bg-gray-50 text-gray-900 text-sm outline-none mb-6 focus:border-violet-400"
              />
              <button
                onClick={handleStep1}
                disabled={busy}
                data-testid="onboarding-step1-next"
                className={`w-full py-3 rounded-lg bg-violet-600 text-white font-bold text-sm flex items-center justify-center gap-2 ${busy ? "opacity-70 cursor-not-allowed" : "hover:bg-violet-700 cursor-pointer"}`}
              >
                {busy ? <Loader2 size={16} className="animate-spin" /> : null}
                Next <ChevronRight size={16} />
              </button>
            </div>
          )}

          {/* ── Step 2: Connect tools ── */}
          {step === 2 && (
            <div>
              <h1 className="text-[22px] font-bold text-gray-900 mb-1.5">Connect your tools</h1>
              <p className="text-[13px] text-gray-500 mb-6 leading-relaxed">
                Optional — connect HubSpot and Xero now and CombinedWork can auto-create projects from
                Closed-Won deals and match them against Xero for cost data. You can always do this later
                from Integrations.
              </p>
              <div className="flex flex-col gap-2.5 mb-6">
                {[
                  { key: "hubspot", label: "HubSpot", desc: "Auto-create projects from Closed-Won deals", href: `${apiBase}/api/integrations/hubspot/connect` },
                  { key: "xero", label: "Xero", desc: "Match projects to Xero for cost data", href: `${apiBase}/api/integrations/xero/connect` },
                ].map((tool) => (
                  <div key={tool.key} className="flex items-center justify-between gap-3 py-3 px-3.5 rounded-lg border border-gray-200 bg-gray-50">
                    <div className="min-w-0">
                      <div className="text-[13px] font-semibold text-gray-900">{tool.label}</div>
                      <div className="text-[11px] text-gray-400 mt-0.5">{tool.desc}</div>
                    </div>
                    {toolsStatus[tool.key] ? (
                      <span className="flex items-center gap-1 text-xs font-semibold text-emerald-600 shrink-0" data-testid={`onboarding-${tool.key}-connected`}>
                        <Check size={14} /> Connected
                      </span>
                    ) : (
                      <button
                        onClick={() => window.open(tool.href, "_blank", "noopener,noreferrer")}
                        data-testid={`onboarding-connect-${tool.key}`}
                        className="flex items-center gap-1 text-xs font-semibold text-violet-600 border border-violet-200 rounded-md px-2.5 py-1.5 hover:bg-violet-50 cursor-pointer shrink-0"
                      >
                        Connect <ExternalLink size={12} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
              {!(toolsStatus.hubspot && toolsStatus.xero) && (
                <button
                  type="button"
                  onClick={checkToolsStatus}
                  disabled={toolsChecking}
                  data-testid="onboarding-recheck-tools"
                  className="bg-transparent border-none text-violet-600 text-xs font-medium cursor-pointer p-0 mb-5 block"
                >
                  {toolsChecking ? "Checking…" : "Connected in another tab? Refresh status"}
                </button>
              )}
              <button
                onClick={() => setStep(3)}
                disabled={busy}
                data-testid="onboarding-step2-next"
                className={`w-full py-3 rounded-lg bg-violet-600 text-white font-bold text-sm flex items-center justify-center gap-2 ${busy ? "opacity-70 cursor-not-allowed" : "hover:bg-violet-700 cursor-pointer"}`}
              >
                Continue <ChevronRight size={16} />
              </button>
            </div>
          )}

          {/* ── Step 3: Project ── */}
          {step === 3 && (
            <div>
              <h1 className="text-[22px] font-bold text-gray-900 mb-1.5">Create your first project</h1>
              <p className="text-[13px] text-gray-500 mb-6 leading-relaxed">This will be your main workspace. You can create more later.</p>
              <label className="block text-xs text-gray-500 mb-1.5 font-medium">Project name</label>
              <input
                type="text"
                value={projectName}
                onChange={(e) => setProjectName(e.target.value)}
                placeholder="e.g. Q3 Product Launch"
                autoFocus
                data-testid="onboarding-project-name"
                className="w-full box-border py-2.5 px-3.5 rounded-lg border border-gray-200 bg-gray-50 text-gray-900 text-sm outline-none mb-5 focus:border-violet-400"
              />
              <label className="block text-xs text-gray-500 mb-2.5 font-medium">Methodology</label>
              <div className="flex flex-col gap-2 mb-6">
                {METHODOLOGIES.map((m) => (
                  <button
                    key={m.value}
                    onClick={() => setMethodology(m.value)}
                    data-testid={`onboarding-methodology-${m.value}`}
                    className={`py-3 px-3.5 rounded-lg text-left flex items-center justify-between cursor-pointer border ${
                      methodology === m.value ? "border-violet-600 bg-violet-50" : "border-gray-200 bg-gray-50"
                    }`}
                  >
                    <div>
                      <div className={`text-[13px] font-semibold ${methodology === m.value ? "text-violet-600" : "text-gray-900"}`}>{m.label}</div>
                      <div className="text-[11px] text-gray-400 mt-0.5">{m.desc}</div>
                    </div>
                    {methodology === m.value && <Check size={14} className="text-violet-600 shrink-0" />}
                  </button>
                ))}
              </div>
              <button
                type="button"
                onClick={() => setShowPhases((v) => !v)}
                data-testid="onboarding-toggle-phases"
                className="bg-transparent border-none text-violet-600 text-xs font-medium cursor-pointer p-0 mb-3 block"
              >
                {showPhases ? "Hide" : "+ Add delivery phases (optional)"}
              </button>
              {showPhases && (
                <textarea
                  value={phasesText}
                  onChange={(e) => setPhasesText(e.target.value)}
                  placeholder={"Discovery\nDesign\nBuild\nLaunch"}
                  rows={4}
                  data-testid="onboarding-phases-input"
                  className="w-full box-border py-2.5 px-3.5 rounded-lg border border-gray-200 bg-gray-50 text-gray-900 text-[13px] outline-none mb-5 font-mono resize-y focus:border-violet-400"
                />
              )}
              <button
                onClick={handleStep3}
                disabled={busy}
                data-testid="onboarding-step3-next"
                className={`w-full py-3 rounded-lg bg-violet-600 text-white font-bold text-sm flex items-center justify-center gap-2 ${busy ? "opacity-70 cursor-not-allowed" : "hover:bg-violet-700 cursor-pointer"}`}
              >
                {busy ? <Loader2 size={16} className="animate-spin" /> : null}
                Create project <ChevronRight size={16} />
              </button>
            </div>
          )}

          {/* ── Step 4: Invite ── */}
          {step === 4 && (
            <div>
              <h1 className="text-[22px] font-bold text-gray-900 mb-1.5">Invite a teammate</h1>
              <p className="text-[13px] text-gray-500 mb-6 leading-relaxed">You can always do this later from the Team page.</p>
              <label className="block text-xs text-gray-500 mb-1.5 font-medium">Email address</label>
              <input
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="colleague@company.com"
                autoFocus
                data-testid="onboarding-invite-email"
                className="w-full box-border py-2.5 px-3.5 rounded-lg border border-gray-200 bg-gray-50 text-gray-900 text-sm outline-none mb-4 focus:border-violet-400"
              />
              <label className="block text-xs text-gray-500 mb-1.5 font-medium">Role</label>
              <select
                value={inviteRole}
                onChange={(e) => setInviteRole(e.target.value)}
                data-testid="onboarding-invite-role"
                className="w-full box-border py-2.5 px-3.5 rounded-lg border border-gray-200 bg-gray-50 text-gray-900 text-sm outline-none mb-6 appearance-none focus:border-violet-400"
              >
                {ROLES.map((r) => (
                  <option key={r} value={r.toLowerCase()}>{r}</option>
                ))}
              </select>
              <div className="flex flex-col gap-2.5">
                <button
                  onClick={() => finishOnboarding(true)}
                  disabled={busy || !inviteEmail.trim()}
                  data-testid="onboarding-send-invite"
                  className={`w-full py-3 rounded-lg font-bold text-sm flex items-center justify-center gap-2 ${
                    !inviteEmail.trim() ? "bg-gray-100 text-gray-400 cursor-not-allowed" : busy ? "bg-violet-600 text-white opacity-70 cursor-not-allowed" : "bg-violet-600 text-white hover:bg-violet-700 cursor-pointer"
                  }`}
                >
                  {busy ? <Loader2 size={16} className="animate-spin" /> : null}
                  Send invite &amp; go to workspace
                </button>
                <button
                  onClick={() => finishOnboarding(false)}
                  disabled={busy}
                  data-testid="onboarding-skip"
                  className={`w-full py-3 rounded-lg bg-transparent text-gray-500 font-medium text-[13px] border border-gray-200 ${busy ? "cursor-not-allowed" : "cursor-pointer hover:border-gray-300"}`}
                >
                  Skip for now →
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OnboardingWizard;
